// File: frontend/src/contexts/ThemeContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLanguage } from './LanguageContext';

const ThemeContext = createContext();

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

export const ThemeProvider = ({ children }) => {
  const { t } = useLanguage();
  
  // Initialize theme from localStorage or default to system preference
  const [themePreference, setThemePreference] = useState(() => {
    const saved = localStorage.getItem('theme-preference');
    return saved || THEMES.SYSTEM;
  });
  
  // Determine the actual theme based on preference and system settings
  const [activeTheme, setActiveTheme] = useState(THEMES.LIGHT);
  
  // Effect to handle system preference changes
  useEffect(() => {
    const updateThemeBasedOnPreference = () => {
      if (themePreference === THEMES.SYSTEM) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setActiveTheme(prefersDark ? THEMES.DARK : THEMES.LIGHT);
      } else {
        setActiveTheme(themePreference);
      }
    };
    
    updateThemeBasedOnPreference();
    
    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (themePreference === THEMES.SYSTEM) {
        updateThemeBasedOnPreference();
      }
    };
    
    // Add event listener with compatibility for older browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // For older browsers
      mediaQuery.addListener(handleChange);
    }
    
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // For older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [themePreference]);
  
  // Effect to apply the theme to the document
  useEffect(() => {
    localStorage.setItem('theme-preference', themePreference);
    document.documentElement.setAttribute('data-theme', activeTheme);
    
    // Apply additional theme-specific styles if needed
    if (activeTheme === THEMES.DARK) {
      document.documentElement.classList.add('dark-theme');
      document.documentElement.classList.remove('light-theme');
    } else {
      document.documentElement.classList.add('light-theme');
      document.documentElement.classList.remove('dark-theme');
    }
  }, [themePreference, activeTheme]);

  const setTheme = (newTheme) => {
    if (Object.values(THEMES).includes(newTheme)) {
      setThemePreference(newTheme);
    } else {
      console.warn(`Theme "${newTheme}" is not supported.`);
    }
  };

  const toggleTheme = () => {
    if (themePreference === THEMES.LIGHT) {
      setThemePreference(THEMES.DARK);
    } else if (themePreference === THEMES.DARK) {
      setThemePreference(THEMES.LIGHT);
    } else {
      // If system preference, toggle to explicit light/dark
      setThemePreference(activeTheme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT);
    }
  };
  
  // Get theme name for display
  const getThemeName = (theme) => {
    switch (theme) {
      case THEMES.LIGHT:
        return t('theme_light') || 'Light';
      case THEMES.DARK:
        return t('theme_dark') || 'Dark';
      case THEMES.SYSTEM:
        return t('theme_system') || 'System';
      default:
        return theme;
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme: activeTheme,
      themePreference,
      setTheme,
      toggleTheme,
      getThemeName,
      isLight: activeTheme === THEMES.LIGHT,
      isDark: activeTheme === THEMES.DARK,
      isSystem: themePreference === THEMES.SYSTEM
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
